import React, {useState, useEffect} from 'react';
import {Modal, Button, Input, InputNumber, Result, Select, Switch, Form, message, Card, Typography, Radio} from 'antd';
import campaignService from '../../../services/campaignService';
import countryService from '../../../services/countryService';
import cityService from '../../../services/cityService';
import stateService, {StateOption} from '../../../services/stateService';
import walletService from '../../../services/walletService';
import {useNavigate} from 'react-router-dom';
import {formatValue, useIsMobile} from '../../../utils/helpers';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import DepositForm from '../../Wallet/walletDetails/depositForm';
import {Upload} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import RichText from '../../../components/molecules/Forms/RichText/component';
import DOMPurify from "dompurify";
import GeoRadiusPicker from '../../../components/organisms/Forms/GeoRadiusPicker/component';
import {buildGeographicLocation, TargetingMode, validateClickRate} from '../../../utils/campaignGeo';
import {trackEvent} from '../../../analytics/posthog';

const stripePK = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise: Promise<Stripe | null> = loadStripe(stripePK);

const {Title} = Typography;
const {Option} = Select;
const US_COUNTRY_ISO2 = 'US';

const CreateCampaign: React.FC = () => {
    const [form] = Form.useForm();
    const [isGlobal, setIsGlobal] = useState(true);
    const [targetingMode, setTargetingMode] = useState<TargetingMode>('location');
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [selectedState, setSelectedState] = useState<string | null>(null);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [countries, setCountries] = useState<{ iso2: string; name: string }[]>([]);
    const [states, setStates] = useState<StateOption[]>([]);
    const [cities, setCities] = useState<string[]>([]);
    const [selectedCampaignData, setSelectedCampaignData] = useState<any | null>(null);
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const navigate = useNavigate();

    const [showDepositModal, setShowDepositModal] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [depositStep, setDepositStep] = useState<'input' | 'pay' | 'result'>('input');
    const [paymentOk, setPaymentOk] = useState(false);

    const isMobile = useIsMobile();
    const [showAssets, setShowAssets] = useState(false);

    const budget = Form.useWatch('initialBudget', form);
    const insufficientFunds = typeof budget === 'number' && budget > walletBalance;
    const [depositForm] = Form.useForm();
    const isUnitedStatesSelected = selectedCountry === US_COUNTRY_ISO2;

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [videoFile, setVideoFile] = useState<File | null>(null);

    useEffect(() => {
        trackEvent('advertiser_create_campaign_page_viewed', {
            role: 'advertiser'
        });
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const data = await walletService.getWallet();
                setWalletBalance(data.balance);
            } catch (err) {
                console.error('Failed to load wallet balance', err);
            }
        })();
    }, []);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await countryService.getCountries();
                setCountries(data);
            } catch (error) {
                console.error('Error fetching countries:', error);
                message.error('Failed to load countries.');
            }
        };
        fetchCountries();
    }, []);

    const handleSubmit = async (values: any) => {
        if (submitting) return;

        const geographicLocation = buildGeographicLocation(values, isGlobal, targetingMode);

        const formData = new FormData();

        const safeToStoreDescription = DOMPurify.sanitize(values.description || '', {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'],
            ALLOWED_ATTR: [],
        });

        formData.append('title', values.title);
        formData.append('description', safeToStoreDescription);
        formData.append('targetURL', values.targetURL);
        formData.append('clickRate', String(values.clickRate));
        formData.append('initialBudget', String(values.initialBudget));
        formData.append('geographicLocation', JSON.stringify(geographicLocation));
        formData.append('reviewNeeded', String(values.reviewNeeded));

        // Append files if they exist
        if (logoFile) {
            formData.append('logo', logoFile);
        }
        if (thumbnailFile) {
            formData.append('thumbnail', thumbnailFile);
        }
        if (mediaFile) {
            formData.append('media', mediaFile);
        }
        if (videoFile) {
            formData.append('video', videoFile);
        }

        setSubmitting(true);
        try {
            const response = await campaignService.createCampaign(formData);
            const createdCampaign = response?.data?.campaign || response;
            const campaignId = createdCampaign._id || createdCampaign.id;

            if (campaignId) {
                setSelectedCampaignData({...createdCampaign, _id: campaignId});
                message.success('Campaign created successfully!');
                // Reset form and file states
                form.resetFields();
                setLogoFile(null);
                setThumbnailFile(null);
                setMediaFile(null);
                setVideoFile(null);
            } else {
                message.error('Failed to create campaign. Please try again.');
            }
        } catch (error: any) {
            console.error('Error creating campaign:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create campaign. Please try again.';
            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };


    const handleCountryChange = async (iso2: string) => {
        const countryObj = countries.find(country => country.iso2 === iso2);
        if (!countryObj) return;
        setSelectedCountry(countryObj.iso2);
        setCities([]);
        setStates([]);

        if (countryObj.iso2 === US_COUNTRY_ISO2) {
            const stateData = await stateService.getStatesByCountry(countryObj.iso2);
            setStates(stateData);
        }

        if (countryObj.iso2 !== US_COUNTRY_ISO2) {
            try {
                const cityData = await cityService.getCitiesByCountry(countryObj.iso2);
                setCities(cityData);
            } catch (error) {
                console.error('Error fetching cities:', error);
                message.error('Failed to load cities.');
            }
        }

        setSelectedCity(null);
        setSelectedState(null);
        form.setFieldsValue({state: null, city: null});
    };

    const handleStateChange = async (value?: string) => {
        const state = value || null;
        setSelectedState(state);
        setSelectedCity(null);
        setCities([]);
        form.setFieldsValue({state, city: null});

        if (!state || selectedCountry !== US_COUNTRY_ISO2) return;

        try {
            const cityData = await cityService.getCitiesByCountry(US_COUNTRY_ISO2, state);
            setCities(cityData);
        } catch (error) {
            console.error('Error fetching cities:', error);
            message.error('Failed to load cities.');
        }
    };

    const openDeposit = () => {
        setClientSecret(null);
        setDepositStep('input');
        setShowDepositModal(true);
    };

    const closeDeposit = () => {
        setShowDepositModal(false);
        depositForm.resetFields();
    };

    const onDepositAmountSubmit = async ({amount}: { amount: number }) => {
        if (!amount || amount <= 0) return;
        try {
            const res = await walletService.depositFunds({amount});
            setClientSecret(res.clientSecret);
            setDepositStep('pay');
        } catch (e: any) {
            message.error(e.message || 'Error creating payment intent');
            closeDeposit();
        }
    };

    const refreshBalance = async () => {
        try {
            const data = await walletService.getWallet();
            setWalletBalance(data.balance);
            await form.validateFields(['initialBudget']).catch();
        } catch {
        }
    };

    const beforeUpload = (file: File) => {
        const fileName = file.name.toLowerCase();

        const isVideo =
            file.type.startsWith('video/') ||
            fileName.endsWith('.mp4') ||
            fileName.endsWith('.mov') ||
            fileName.endsWith('.webm');

        const limit = isVideo
            ? 100 * 1024 * 1024 // 100MB
            : 5 * 1024 * 1024;  // 5MB images

        if (file.size > limit) {
            message.error(
                isVideo
                    ? 'Video must be smaller than 100 MB'
                    : 'Image must be smaller than 5 MB'
            );
            return Upload.LIST_IGNORE;
        }

        return false;
    };

    useEffect(() => {
        if (selectedCampaignData?._id) {
            navigate(`/campaigns/${selectedCampaignData._id}`);
        }
    }, [selectedCampaignData, navigate]);

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: isMobile ? 12 : 20}}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 650,
                    margin: '0 auto',
                    padding: isMobile ? 12 : 24,
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: 10,
                }}
                bodyStyle={{padding: isMobile ? 12 : 24}}
            >
                <Title level={4} className={"title"}>Create campaign</Title>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{isGlobal: true, reviewNeeded: false}}>
                    <Form.Item label="Title" name="title" rules={[{required: true, message: 'Title is required'}]}>
                        <Input/>
                    </Form.Item>

                    <Form.Item label="Description" name="description">
                        <RichText placeholder="Write a description about your campaign..."/>
                    </Form.Item>

                    <Form.Item
                        label="Target URL"
                        name="targetURL"
                        rules={[
                            {required: true, message: 'Target URL is required'},
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    try {
                                        new URL(value);
                                        return Promise.resolve();
                                    } catch {
                                        return Promise.reject(new Error('Enter a valid URL (e.g., https://example.com)'));
                                    }
                                }
                            }
                        ]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        label="Click rate ($ per click)"
                        name="clickRate"
                        dependencies={["country", "state", "city", "geoRadius"]}
                        rules={[
                            {required: true, message: "Click rate is required"},
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    const country = getFieldValue("country");
                                    const state = getFieldValue("state");
                                    const city = getFieldValue("city");
                                    const geoRadius = getFieldValue("geoRadius");

                                    return validateClickRate(value, country, state, city, geoRadius);
                                },
                            }),
                        ]}
                    >
                        <Input type="number" step="0.01"/>
                    </Form.Item>

                    <Form.Item
                        label="Initial budget ($)"
                        name="initialBudget"
                        validateStatus={insufficientFunds ? "error" : ""}
                        rules={[
                            {required: true, message: "Initial budget is required"},
                            {
                                validator: (_, value) => {
                                    if (value == null) return Promise.resolve();
                                    if (value < 10) return Promise.reject(new Error("Budget must be at least $10"));
                                    return Promise.resolve();
                                }
                            }
                        ]}
                        getValueFromEvent={(e) => {
                            const raw = e?.target?.value;
                            if (raw === '' || raw == null) return undefined;
                            const n = Number(raw);
                            return Number.isNaN(n) ? undefined : n;
                        }}
                        help={
                            insufficientFunds ? (
                                <span>
                                    Insufficient funds: you have ${formatValue(walletBalance)}{' '}
                                    <Button type="link" size="small" onClick={() => openDeposit()}>Deposit</Button>
                                </span>
                            ) : null
                        }
                    >
                        <Input type="number" step="1"/>
                    </Form.Item>

                    <Form.Item
                        label="Review needed"
                        name="reviewNeeded"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </Form.Item>

                    <Form.Item label="Geographic targeting">
                        <Switch checked={isGlobal} onChange={(checked) => {
                            setIsGlobal(checked);
                            if (checked) {
                                setSelectedCountry(null);
                                setSelectedState(null);
                                setSelectedCity(null);
                                setTargetingMode('location');
                                form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                            }
                        }} checkedChildren="Global" unCheckedChildren="Custom"/>
                    </Form.Item>

                    {!isGlobal && (
                        <>
                            <Form.Item label="Targeting method">
                                <Radio.Group
                                    value={targetingMode}
                                    onChange={(event) => {
                                        const nextMode = event.target.value;
                                        setTargetingMode(nextMode);
                                        if (nextMode === 'map') {
                                            setSelectedCountry(null);
                                            setSelectedState(null);
                                            setSelectedCity(null);
                                            setStates([]);
                                            setCities([]);
                                            form.setFieldsValue({country: null, state: null, city: null, geoRadius: null});
                                        } else {
                                            form.setFieldsValue({geoRadius: null});
                                        }
                                    }}
                                >
                                    <Radio.Button value="location">Country / state / city</Radio.Button>
                                    <Radio.Button value="map">Pick on map</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {targetingMode === 'location' ? (
                                <>
                                    <Form.Item label="Country" name="country"
                                               rules={[{required: true, message: "Select a country"}]}>
                                        <Select showSearch value={selectedCountry}
                                                onChange={(iso2) => handleCountryChange(iso2)}
                                                placeholder="Country">
                                            {countries.map((country) => (
                                                <Option key={country.iso2} value={country.iso2}>{country.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    {isUnitedStatesSelected && (
                                        <Form.Item label="State" name="state"
                                                   rules={[{required: true, message: "Select a state"}]}>
                                            <Select showSearch value={selectedState} onChange={handleStateChange} placeholder="State">
                                                {states.map((state) => (
                                                    <Option key={state.code} value={state.name}>{state.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}
                                    <Form.Item label="City" name="city">
                                        <Select showSearch allowClear value={selectedCity} onChange={(value) => {
                                            setSelectedCity(value || null);
                                            form.setFieldsValue({city: value || null});
                                        }} placeholder="City (Optional)" disabled={!selectedCountry || (isUnitedStatesSelected && !selectedState)}>
                                            {cities.map((city) => (
                                                <Option key={city} value={city}>{city}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </>
                            ) : (
                                <Form.Item
                                    label="Target area"
                                    name="geoRadius"
                                    rules={[{required: true, message: "Choose a location and radius"}]}
                                >
                                    <GeoRadiusPicker/>
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item label="Campaign assets">
                        <Switch
                            checked={showAssets}
                            onChange={(checked) => {
                                setShowAssets(checked);

                                if (!checked) {
                                    setLogoFile(null);
                                    setThumbnailFile(null);
                                    setMediaFile(null);
                                    setVideoFile(null);
                                }
                            }}
                            checkedChildren="Show"
                            unCheckedChildren="Hide"
                        />
                    </Form.Item>

                    {showAssets && (
                        <div style={{
                            padding: isMobile ? 10 : 12, border: '1px solid #f0f0f0',
                            borderRadius: 8, marginBottom: 12, background: '#fafafa',
                        }}>
                            <Form.Item label="Logo" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    onRemove={() => setLogoFile(null)}
                                    onChange={(info) => {
                                        const {file} = info;
                                        if (file.status === 'removed') {
                                            setLogoFile(null);
                                            return;
                                        }
                                        const fileObj = file.originFileObj || file;
                                        if (fileObj instanceof File) setLogoFile(fileObj);
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>
                                        Upload logo
                                    </Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Thumbnail" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    onRemove={() => setThumbnailFile(null)}
                                    onChange={(info) => {
                                        const {file} = info;
                                        if (file.status === 'removed') {
                                            setThumbnailFile(null);
                                            return;
                                        }
                                        const fileObj = file.originFileObj || file;
                                        if (fileObj instanceof File) setThumbnailFile(fileObj);
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>
                                        Upload thumbnail
                                    </Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Media" extra="Image only • Max 5 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="image/*"
                                    onRemove={() => setMediaFile(null)}
                                    onChange={(info) => {
                                        const {file} = info;
                                        if (file.status === 'removed') {
                                            setMediaFile(null);
                                            return;
                                        }
                                        const fileObj = file.originFileObj || file;
                                        if (fileObj instanceof File) setMediaFile(fileObj);
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined/>}>
                                        Upload media
                                    </Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item label="Video" extra="MP4 • Max 100 MB">
                                <Upload
                                    beforeUpload={beforeUpload}
                                    maxCount={1}
                                    accept="video/mp4"
                                    onRemove={() => setVideoFile(null)}
                                    onChange={(info) => {
                                        const fileObj = info.file.originFileObj || info.file;

                                        if (fileObj instanceof File) {
                                            setVideoFile(fileObj);
                                        }
                                    }}
                                >
                                    <Button block={isMobile} icon={<UploadOutlined />}>
                                        Upload video
                                    </Button>
                                </Upload>
                            </Form.Item>
                        </div>
                    )}

                    <Form.Item style={{textAlign: 'right'}} shouldUpdate>
                        {() => {
                            const hasErrors = form.getFieldsError().some(({errors}) => errors.length);
                            const budget = form.getFieldValue('initialBudget');
                            const insufficientFunds = typeof budget === 'number' && budget > walletBalance;
                            return (
                                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block={isMobile}
                                        disabled={hasErrors || insufficientFunds || submitting}
                                        loading={submitting}
                                        style={{width: isMobile ? '100%' : 'auto'}}
                                    >
                                        {submitting ? 'Creating...' : 'Create campaign'}
                                    </Button>
                                </div>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Card>

            <Modal
                open={showDepositModal}
                title="Deposit Funds"
                onCancel={closeDeposit}
                footer={null}
                destroyOnClose
                forceRender
                width={isMobile ? '92vw' : 520}
                styles={{body: {padding: isMobile ? 12 : 24}}}
                afterOpenChange={(open) => {
                    if (open && depositStep === 'input') {
                        const initial = Number(form.getFieldValue('initialBudget') ?? 0);
                        const neededRaw = initial - (walletBalance ?? 0);
                        const prefill = Math.max(20, Math.round(Math.max(0, neededRaw) * 100) / 100);
                        depositForm.setFieldsValue({amount: prefill});
                    }
                }}
            >
                {depositStep === 'input' && (
                    <Form form={depositForm} layout="vertical" onFinish={onDepositAmountSubmit}>
                        <Form.Item
                            label="Amount (USD)"
                            name="amount"
                            rules={[
                                {required: true, message: 'Please enter an amount'},
                                {
                                    validator: (_, v) =>
                                        v >= 20 ? Promise.resolve() : Promise.reject(new Error('Minimum deposit is $20')),
                                },
                            ]}
                        >
                            <InputNumber style={{width: '100%'}} min={0}/>
                        </Form.Item>
                        <div style={{textAlign: 'right'}}>
                            <Button onClick={closeDeposit} style={{marginRight: 8}}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Continue
                            </Button>
                        </div>
                    </Form>
                )}

                {depositStep === 'pay' && clientSecret && (
                    <Elements stripe={stripePromise} options={{clientSecret}}>
                        <DepositForm
                            onSuccess={() => {
                                setPaymentOk(true);
                                setDepositStep('result');
                            }}
                            onError={() => {
                                setPaymentOk(false);
                                setDepositStep('result');
                            }}
                        />
                    </Elements>
                )}

                {depositStep === 'result' &&
                    (paymentOk ? (
                        <Result
                            status="success"
                            title="Deposit successful!"
                            extra={[
                                <Button
                                    key="back"
                                    type="primary"
                                    onClick={async () => {
                                        closeDeposit();
                                        await refreshBalance();
                                        message.success('Balance updated');
                                    }}
                                >
                                    Back
                                </Button>,
                            ]}
                        />
                    ) : (
                        <Result
                            status="error"
                            title="Payment failed"
                            subTitle="Please try again."
                            extra={[
                                <Button key="retry" onClick={() => setDepositStep('input')}>
                                    Try Again
                                </Button>,
                            ]}
                        />
                    ))}
            </Modal>
        </div>
    );
};

export default CreateCampaign;